import React, { useEffect, useRef, useState, useMemo } from "react";
import "./sideCardTab.css";

function SideCardTabComponent({ tabConfig, titleForSideCard }) {
  const [activeTab, setActiveTab] = useState(Object.keys(tabConfig)[0] || "");
  const sectionRefs = useRef({});
  const contentAreaRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    // Reset scroll position on mount and when tabConfig changes
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
      setActiveTab(Object.keys(tabConfig)[0] || ""); // Also reset to first tab
    }
  }, [tabConfig]); // Reset when tab configuration changes
  // Create or update refs whenever tabConfig changes
  useMemo(() => {
    const enabledTabs = Object.keys(tabConfig).filter(tab => !tabConfig[tab]?.isDisabled);
    
    // Create new refs for new tabs, keep existing ones
    const newRefs = {};
    enabledTabs.forEach(tab => {
      newRefs[tab] = {
        name: tabConfig[tab].title,
        ref: sectionRefs.current[tab]?.ref || React.createRef()
      };
    });
    
    sectionRefs.current = newRefs;
  }, [tabConfig]);

  const handleTabClick = (tabId) => {
    if (!sectionRefs.current[tabId]?.ref.current) return;
    
    setActiveTab(tabId);
    sectionRefs.current[tabId].ref.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;

    const handleScroll = () => {
      // Debounce scroll events
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const scrollPosition = contentArea.scrollTop + 100;
        
        // Find which section is currently in view
        for (const [tab, refObj] of Object.entries(sectionRefs.current)) {
          const section = refObj.ref.current;
          if (!section) continue;

          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;
          
          if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
            setActiveTab(tab);
            break;
          }
        }
      }, 100);
    };

    contentArea.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      contentArea.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!Object.keys(tabConfig).length) return null;

  return (
    <div className="nudge-overview-container pt-3" style={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      height: "100%",
    }}>
      <div className="side-tabs" style={{
        width: "16rem",
        maxWidth: "16rem",
        backgroundColor: "#f7f8f9",
        borderRadius: "1vh",
        padding: "1vh",
        borderRight: "0.1rem solid #E2E8F0",
      }}>
        {Object.entries(tabConfig).map(([tab, config]) => (
          !config?.isDisabled && (
            <button
              key={tab}
              onClick={() => handleTabClick(tab)}
              className={`tabBtnForSideCard ${activeTab === tab && "active"}`}
            >
              {config.title}
            </button>
          )
        ))}
      </div>

      <div
        ref={contentAreaRef}
        className="content-area"
        style={{
          flex: 1,
          overflowY: "auto",
          height: "100%",
          position: "relative",
          maxWidth: "60vw",
        }}
      >
        {titleForSideCard}

        {Object.entries(tabConfig).map(([tab, config]) => (
          !config?.isDisabled && (
            <section
              key={tab}
              ref={sectionRefs.current[tab]?.ref}
              id={`section-${tab}`}
              style={{
                maxWidth: "60vw",
                minHeight: "40vh",
                borderRadius: "4px",
                backgroundColor: "#fff",
                margin: "1rem",
              }}
            >
              {config.content}
            </section>
          )
        ))}
      </div>
    </div>
  );
}

export default React.memo(SideCardTabComponent);